import { CloseOutlined } from '@ant-design/icons'
import { useOperationsRequests } from '@api/useOperationsRequests'
import { RequestDrawerViewOnly } from '@components/OrderDispatch/OperationsRequests/RequestDrawerViewOnly'
import { RichTextEditor } from '@components/OrderDispatch/OperationsRequests/RichTextEditor'
import {
  BBDTag,
  ErrorNotification,
  GraviButton,
  Horizontal,
  MomentDatePicker,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Drawer, Form, Input, Popconfirm, Select } from 'antd'
import moment from 'moment'
import React, { useEffect, useState } from 'react'

export function RequestDrawer({
  visible,
  setVisible,
  requestItem,
  setRequestItem,
  sites,
  operationTypes,
  ownerOptions,
  editRequestScope,
}) {
  const { editRequestMutation } = useOperationsRequests()
  const [form] = Form.useForm()
  const [richValue, setRichValue] = useState(requestItem?.note || null)
  const [confirmSendVisible, setConfirmSendVisible] = useState(false)
  const canEdit = editRequestScope && requestItem?.status !== 'complete'
  const [toDate, setToDate] = useState(requestItem?.to_date ? moment(requestItem?.to_date) : null)
  const [fromDate, setFromDate] = useState(requestItem?.from_date ? moment(requestItem?.from_date) : null)
  const [loading, setLoading] = useState(false)

  const options = ownerOptions?.map((owner) => {
    const value = owner.flat()
    const label =
      value[1] === 'driver' ? value[0] : value[1] === null ? `${value[0]} - No Email` : `${value[0]} - ${value[1]}`

    return {
      label,
      value: value[0],
      disabled: value[1] === null,
    }
  })
  const onFinishForm = (formData) => {
    setLoading(true)
    if (formData.owner) {
      setConfirmSendVisible(true)
      return
    }
    submitForm(formData)
  }
  const closeAndReset = () => {
    setVisible(false)
    setRequestItem(null)
    form.resetFields()
    setToDate(null)
    setFromDate(null)
    setLoading(false)
  }
  const submitForm = async (formData) => {
    const payload = {
      ...requestItem,
      ...formData,
      from_date: moment(formData.from_date).toISOString(),
      to_date: moment(formData.to_date).toISOString(),
      status: 'open',
      number: requestItem?.number || null,
      id: requestItem?._id || null,
    }
    try {
      await editRequestMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Operations Request Form Submission Error: ', { error })
    } finally {
      closeAndReset()
      setLoading(false)
    }
  }

  useEffect(() => {
    if (visible && requestItem) {
      form.setFieldsValue(requestItem)
      setToDate(requestItem?.to_date ? moment(requestItem?.to_date) : null)
      setFromDate(requestItem?.from_date ? moment(requestItem?.from_date) : null)
    }
  }, [visible])

  return (
    <Drawer
      destroyOnClose
      title={
        <Horizontal justifyContent='space-between' alignItems='center'>
          <Texto category='heading'> NEW OPERATIONS REQUEST </Texto>
          <GraviButton
            hoverTitle='Close'
            icon={<CloseOutlined />}
            onClick={closeAndReset}
            style={{ background: 'transparent' }}
          />
        </Horizontal>
      }
      visible={visible}
      placement='bottom'
      onClose={closeAndReset}
      height='80vh'
      closable={false}
      style={{ background: 'var(--bg-2)' }}
    >
      {!canEdit ? (
        <RequestDrawerViewOnly requestItem={requestItem} />
      ) : (
        <Form
          style={{ height: '100%' }}
          form={form}
          onFinish={onFinishForm}
          onFinishFailed={() =>
            ErrorNotification({
              errorFields: [
                {
                  errors: [`Form was unable to be saved.`],
                },
              ],
            })
          }
        >
          <Vertical justifyContent='space-between' className='full-height-width'>
            <div style={{ overflowY: 'auto' }}>
              <div>
                <Vertical>
                  <hr className='operations-hr-bold' />
                  <Horizontal className='mr-4 ml-4 bg-2' alignItems='space-between' justifyContent='space-evenly'>
                    <Vertical className='p-4' flex={2}>
                      <Texto className='pb-2'>Title</Texto>
                      <Form.Item name='subject' rules={[{ required: true, message: 'Title is required.' }]}>
                        <Input />
                      </Form.Item>
                    </Vertical>
                    <Vertical className='p-4' flex={1.5}>
                      <Texto className='pb-2'>Site</Texto>
                      <Form.Item name='site_id' rules={[{ required: false }]}>
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp='label'
                          options={sites?.map((s) => ({ label: s[0], value: s[1] })) || []}
                          filterSort={(optionA, optionB) =>
                            parseInt(optionA?.label ?? '0') - parseInt(optionB?.label ?? '0')
                          }
                        />
                      </Form.Item>
                    </Vertical>
                    <Vertical className='p-4' flex={1.5}>
                      <Texto className='pb-2'>Type</Texto>
                      <Form.Item name='type' rules={[{ required: true, message: 'Type is required.' }]}>
                        <Select options={operationTypes?.map((ot) => ({ label: ot, value: ot })) || []} />
                      </Form.Item>
                    </Vertical>
                    <Vertical className='p-4' flex={0.75}>
                      <Texto className='pb-2'>From Date</Texto>
                      <Form.Item name='from_date' rules={[{ required: true, message: 'From Date is required.' }]}>
                        <MomentDatePicker format={formatters.DATE} />
                      </Form.Item>
                    </Vertical>
                    <Vertical className='p-4' flex={0.75}>
                      <Texto className='pb-2'>To Date </Texto>
                      <Form.Item name='to_date' rules={[{ required: true, message: 'To Date is required.' }]}>
                        <MomentDatePicker format={formatters.DATE} />
                      </Form.Item>
                    </Vertical>
                    <Vertical className='p-4' flex={0.75}>
                      <Texto className='pb-2'>Status</Texto>
                      {canEdit || requestItem?.status === 'open' ? (
                        <BBDTag className='pt-1 pb-1 mt-1' theme2>
                          Open
                        </BBDTag>
                      ) : (
                        <BBDTag success> Complete </BBDTag>
                      )}
                    </Vertical>
                  </Horizontal>
                  <hr className='operations-hr' />
                </Vertical>
              </div>

              <div className='full-width-height'>
                <Horizontal className='p-4 m-4' horizontalCenter verticalCenter style={{ minHeight: '400px' }}>
                  <Vertical className='mr-4 ml-4' style={{ maxWidth: '1250px' }}>
                    <Form.Item name='note' rules={[{ required: true, message: 'Description is required.' }]}>
                      <RichTextEditor value={richValue} onChange={(value) => setRichValue(value)} />
                    </Form.Item>
                  </Vertical>
                </Horizontal>
              </div>
              <Horizontal bg='primary' className='px-3 py-2' border='all' flex='0' style={{ overflow: 'visible' }}>
                <Horizontal gap='16px' flex='1' verticalCenter>
                  <Texto category='h4'>Owner</Texto>

                  <Form.Item className='mb-0' name='owner'>
                    <Select showSearch options={options || []} style={{ width: '250px' }} />
                  </Form.Item>
                </Horizontal>

                <Popconfirm
                  title='Email this request to the owner?'
                  okText='Send'
                  cancelText='Cancel'
                  visible={confirmSendVisible}
                  onConfirm={() => {
                    setConfirmSendVisible(false)
                    return submitForm(form.getFieldsValue())
                  }}
                  onCancel={() => {
                    setConfirmSendVisible(false)
                    setLoading(false)
                  }}
                >
                  <SaveCancelButtons loading={loading} closeAndReset={closeAndReset} />
                </Popconfirm>
              </Horizontal>
            </div>
          </Vertical>
        </Form>
      )}
    </Drawer>
  )
}
export function SaveCancelButtons({ closeAndReset, loading }) {
  return (
    <Horizontal className='p-4' justifyContent='flex-end'>
      <GraviButton disabled={loading} onClick={closeAndReset} buttonText='Cancel' className='mr-4' />
      <GraviButton disabled={loading} theme2 buttonText='Save' htmlType='submit' />
    </Horizontal>
  )
}
